import { CoinsVal } from "@components/icons/coins";
import { ResponsiveSelect } from "@components/responsive-select";
import { CameraModeType } from "@features/camera/modes";
import { LorasType } from "@features/loras/type";
import { useLora } from "@features/loras/use-loras";
import { ProjectType } from "@features/projects/type";
import { useProjects } from "@features/projects/use-projects";
import { useUser } from "@features/user/use-user";
import { FolderIcon } from "@heroicons/react/16/solid";
import { Button, Flex } from "@radix-ui/themes";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { useCameraForm } from "../camera-form-context";
import { FormatIcon } from "./format-icon";
import { useModelConsentModal } from "./model-consent-modal";
import UpgradeNeeded from "@components/upgrade-needed";

// Extracted Footer Component
export const CameraFormFooter = () => {
  const { t } = useTranslation();
  const {
    loading,
    project,
    setProjectId,
    format,
    setFormat,
    effectiveCount,
    count,
    setCount,
    formats,
    mode,
    model,
    handleFormSubmit,
    pack,
    image,
    promptTxt,
  } = useCameraForm();
  const currentLora = useLora(model).data;

  const isFormDisabled =
    loading ||
    mode === "none" ||
    (currentLora && currentLora?.status !== "completed") ||
    (mode === "pack" && pack === "none") ||
    (mode === "classic" && !promptTxt) ||
    (mode === "tryon" && !image) ||
    (mode === "haircut" && !image) ||
    (mode === "image" && !image);

  return (
    <div className="space-y-2 px-4">
      <ProjectSelector
        value={project}
        onChange={setProjectId}
        loading={loading}
      />
      <div className="space-x-1 flex flex-row w-full">
        <ResponsiveSelect
          value={format}
          onChange={setFormat}
          size="2"
          className="shrink-0 grow"
          items={Object.entries(formats).map(([key, value]) => ({
            value: key,
            label: (
              <Flex as="span" align="center" gap="2">
                <FormatIcon format={key} />
                {value as string}
              </Flex>
            ),
          }))}
          renderTrigger={() => (
            <Flex as="span" align="center" gap="2">
              <FormatIcon format={format} />
              {formats[format] || t("camera.form.formats.auto")}
            </Flex>
          )}
          disabled={loading || mode === "pack"}
        />
        <ResponsiveSelect
          value={`${count}`}
          onChange={setCount}
          size="2"
          className="shrink-0 w-max"
          items={["1", "2", "4", "8", "16"].map((key) => ({
            value: key,
            label: t("camera.form.take_photos", { replace: { count: key } }),
          }))}
          renderTrigger={(item) => (
            <Flex as="span" align="center" gap="2">
              {t("camera.form.take_photos", {
                replace: { count: effectiveCount },
              })}
            </Flex>
          )}
          disabled={loading || mode === "pack"}
        />
      </div>

      <div>
        <UpgradeNeeded
          minimumCredits={
            getPhotoCost(mode, currentLora || undefined) *
            parseInt(effectiveCount)
          }
        >
          <ButtonWithConsent
            className="w-full"
            model={currentLora || undefined}
            loading={loading}
            disabled={isFormDisabled}
            onClick={handleFormSubmit}
          >
            <span>
              {t("camera.form.run")}{" "}
              <CoinsVal
                val={
                  getPhotoCost(mode, currentLora || undefined) *
                  parseInt(effectiveCount)
                }
              />
            </span>
          </ButtonWithConsent>
        </UpgradeNeeded>
      </div>
    </div>
  );
};

export const ProjectSelector = ({
  value: project,
  onChange: setProjectId,
  loading = false,
}: {
  value: string;
  onChange: (id: string) => void;
  loading?: boolean;
}) => {
  const { projects } = useProjects();
  return (
    <ResponsiveSelect
      value={project}
      onChange={setProjectId}
      size="2"
      className="w-full"
      items={[null, ..._.sortBy(projects.data || [], "name")].map(
        (project) => ({
          value: project?.id || "none",
          label: (
            <Flex as="span" align="center" gap="2">
              {project?.id && <FolderIcon className="h-4 w-4" />}
              {project?.name || "No project"}
            </Flex>
          ),
        })
      )}
      renderTrigger={(item) => {
        const project = (projects.data || [])?.find(
          (a: ProjectType) => a.id === item?.value
        );
        return (
          <Flex as="span" align="center" gap="2">
            {project?.id && <FolderIcon className="h-4 w-4" />}
            {(project?.id ? project?.name : "") || "No project"}
          </Flex>
        );
      }}
      disabled={loading}
    />
  );
};

const ButtonWithConsent = ({
  disabled,
  loading,
  onClick,
  children,
  model,
  className,
}: {
  disabled: boolean;
  loading: boolean;
  onClick: () => void;
  children: React.ReactNode;
  model?: LorasType;
  className?: string;
}) => {
  const { user } = useUser();
  const { setOpen } = useModelConsentModal();

  const handleClick = () => {
    // If no model selected or model is owned by user, proceed
    if (
      !model ||
      model.owner_id === user.data?.id ||
      (user.data?.consent_usage?.models || []).includes(model.id)
    ) {
      onClick();
      return;
    }

    // Otherwise, open consent modal
    setOpen(true, model.id, model.name, onClick);
  };

  return (
    <>
      <Button
        size="3"
        className={twMerge("grow", "hue-animation", className)}
        disabled={disabled}
        loading={loading}
        onClick={handleClick}
      >
        {children}
      </Button>
    </>
  );
};

export const getPhotoCost = (
  mode?: CameraModeType | string,
  lora?: LorasType
): number => {
  return mode === "tryon" || mode === "haircut" || lora?.technology === "images"
    ? 8
    : 2;
};
